import {
  HealthForecastDTO,
  PetParentData,
  ResultDTO,
} from "@func-core/core/domain/view-models/result-view-model";
import { isSessionStorageAvailable } from "@func-core/core/utils/browser-features";
import { fetchApiJson } from "@utils/request";
import { rollbar, RollbarLogArgument } from "@utils/rollbar";

function resultUrl(resultUuid: string): string {
  if (!process.env.GATSBY_API_BASE) {
    throw new Error("Missing GATSBY_API_BASE environment variable");
  }

  return `${process.env.GATSBY_API_BASE}/results/${resultUuid}/`;
}

const PET_PARENT_STORAGE_KEY = "pet_parent_data";

type ResultAdapter = {
  fetchResult: (resultUuid: string) => Promise<ResultDTO | null>;
  fetchHealthForecast: (resultUuid: string) => Promise<HealthForecastDTO | null>;
  savePetParentData: (resultUuid: string, data: Partial<PetParentData>) => void;
  getPetParentData: (resultUuid: string) => PetParentData | null;
};

export const createResultAdapter = (): ResultAdapter => {
  const hasSessionStorage = isSessionStorageAvailable();

  const fetchResult = async (resultUuid: string): Promise<ResultDTO | null> => {
    return fetchApiJson<ResultDTO>(`${resultUrl(resultUuid)}?include=quotes`);
  };

  const fetchHealthForecast = async (resultUuid: string): Promise<HealthForecastDTO | null> => {
    return fetchApiJson<HealthForecastDTO>(`${resultUrl(resultUuid)}health_forecast`);
  };

  const savePetParentData = (resultUuid: string, data: Partial<PetParentData>): void => {
    if (!hasSessionStorage) return;

    try {
      const existingData = getPetParentData(resultUuid) ?? {};
      const updatedData = { ...existingData, ...data };
      sessionStorage.setItem(
        `${PET_PARENT_STORAGE_KEY}_${resultUuid}`,
        JSON.stringify(updatedData)
      );
    } catch (error) {
      rollbar.error("Failed to save pet parent data", error as RollbarLogArgument);
    }
  };

  const getPetParentData = (resultUuid: string): PetParentData | null => {
    if (!hasSessionStorage) return null;

    try {
      const data = sessionStorage.getItem(`${PET_PARENT_STORAGE_KEY}_${resultUuid}`);

      return data ? (JSON.parse(data) as PetParentData) : null;
    } catch (error) {
      rollbar.error("Failed to get pet parent data", error as RollbarLogArgument);

      return null;
    }
  };

  return {
    fetchResult,
    fetchHealthForecast,
    savePetParentData,
    getPetParentData,
  };
};
